import { Button, flash, Modal } from "@xola/ui-kit";
import { AxiosError } from "axios";
import { get } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { BaseSchedule, Resource } from "types";
import { api } from "../../../api/api";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { BaseScheduleInput } from "../../BaseSchedule/BaseScheduleInput";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface EquipmentScheduleProps {
    resource: Resource;
}

interface UpsertSchedulePayload {
    schedule: BaseSchedule;
    force?: boolean;
}

interface DeleteSchedulePayload {
    schedule: BaseSchedule;
    force?: boolean;
}

interface ScheduleError {
    field: {
        schedule: { reason: string; message: string };
    };
}

enum WarningType {
    CREATE_NEW = "create-new-schedule",
    FORCE_UPSERT = "force-upsert",
    DELETE = "delete",
}

export const EquipmentSchedule = ({ resource }: EquipmentScheduleProps) => {
    const invalidate = useInvalidate();
    const [showWarning, setShowWarning] = useState(false);
    const [warningType, setWarningType] = useState<WarningType | null>(null);
    const [upsertingSchedule, setUpsertingSchedule] = useState<BaseSchedule | null>();

    const handleOnSuccess = () => {
        invalidate("resources", resource.id);
        flash.show({ text: "Successfully saved schedule", color: "success" });

        handleCloseWarning();
    };

    const handleOnError = (
        error: AxiosError<ScheduleError>,
        payload: UpsertSchedulePayload | DeleteSchedulePayload,
    ) => {
        const reason = get(error, "response.data.field.schedule.reason");
        const message: string | undefined = get(error, "response.data.field.schedule.message");

        if (reason === "cannot_add_schedule" || reason === "cannot_modify_schedule") {
            setWarningType(WarningType.FORCE_UPSERT);
        }

        if (
            reason === "cannot_delete_schedule" &&
            !payload.force &&
            message?.includes("equipment is already assigned")
        ) {
            /** If `force` is `false` or `undefined`, and error message is related to "equipment is already assigned", we'll retry to forcefully delete it. */
            setWarningType(WarningType.DELETE);
        }

        setUpsertingSchedule(payload.schedule);
        setShowWarning(true);
    };

    const { mutateAsync: upsertSchedule } = useMutation(
        async ({ schedule, force = false }: UpsertSchedulePayload) => {
            const config = force ? { params: { force } } : {};
            await (schedule?.id
                ? api.put(`/resources/${resource.id}/schedules/${schedule.id}`, schedule, config)
                : api.post(`/resources/${resource.id}/schedules`, schedule, config));
        },
        {
            onSuccess: handleOnSuccess,
            onError: handleOnError,
        },
    );

    const { mutateAsync: deleteSchedule } = useMutation(
        async ({ schedule, force = false }: DeleteSchedulePayload) => {
            const config = force ? { params: { force } } : {};

            await api.delete(`/resources/${resource.id}/schedules/${schedule.id}`, config);
            invalidate("resources", resource.id);
            setUpsertingSchedule(null);
            setWarningType(null);
        },
        {
            onError: handleOnError,
        },
    );

    const handleUpsertSchedule = (schedule: BaseSchedule, confirm = false) => {
        const { start, end } = schedule;
        if (start && end && start > end) {
            flash.error("The End Date must come after the Start Date");
            return;
        }

        if ((resource.schedules ?? []).length === 0 && !confirm) {
            /** User is creating a first schedule for the equipment, so we need to show info/warning regarding the side effects for that */
            setUpsertingSchedule(schedule);
            setWarningType(WarningType.CREATE_NEW);
            setShowWarning(true);
            return;
        }

        upsertSchedule({ schedule });
    };

    const handleConfirmClick = () => {
        if (warningType === WarningType.DELETE) {
            deleteSchedule({ schedule: upsertingSchedule!, force: true });
        }

        if (warningType === WarningType.FORCE_UPSERT || warningType === WarningType.CREATE_NEW) {
            upsertSchedule({ schedule: upsertingSchedule!, force: true });
        }

        handleCloseWarning();
    };

    const handleCloseWarning = () => {
        setShowWarning(false);
        setUpsertingSchedule(null);
        setWarningType(null);
    };

    return (
        <>
            <BaseScheduleInput
                schedules={resource.schedules!}
                description="Create a schedule to designate specific days/times that this equipment item is available or unavailable"
                onUpsertSchedule={handleUpsertSchedule}
                onDeleteSchedule={async (schedule) => deleteSchedule({ schedule })}
            />
            <Modal shouldCloseOnOutsideClick isOpen={showWarning} onClose={handleCloseWarning}>
                <Modal.Header>Warning</Modal.Header>
                <Modal.Body className="p1">
                    <div className="space-y-4">
                        {warningType === WarningType.CREATE_NEW && (
                            <>
                                <p>
                                    You are about to create a new schedule for a resource that previously did not have
                                    schedules. This action may result in the following side-effects:
                                </p>
                                <ul className="list-disc space-y-2 pl-8">
                                    <li>Reduction in availability for products associated with this equipment.</li>
                                    <li>
                                        Overbooking due to availability reduction for existing events with bookings.
                                    </li>
                                </ul>
                            </>
                        )}
                        {warningType === WarningType.FORCE_UPSERT && upsertingSchedule && (
                            <p>
                                {upsertingSchedule?.id
                                    ? "Editing an unavailable schedule could result in future events to be overbooked. Are you sure you want to continue?"
                                    : "Creating an unavailable schedule could result in future events to be overbooked. Are you sure you want to continue?"}
                            </p>
                        )}
                        {warningType === WarningType.DELETE && (
                            <p>
                                Deleting this schedule could cause certain future event to be overbooked. Are you sure
                                you want to continue?
                            </p>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="space-x-4">
                    <SecondaryButton onClick={handleCloseWarning}>Cancel</SecondaryButton>
                    <Button color="danger" onClick={handleConfirmClick}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
